import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AiSummaryBody } from '@/types'

export function LetterBody({
  body,
  distressFloorTriggered,
}: {
  body: AiSummaryBody
  distressFloorTriggered: boolean
}) {
  if (distressFloorTriggered) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
          <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
            Important resources
          </span>
        </div>
        <p className="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-line leading-relaxed">
          {body.paragraphs[0]}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {body.paragraphs.filter(Boolean).map((para, i) => (
          <p key={i} className="text-sm leading-relaxed text-foreground/90">
            {para}
          </p>
        ))}
      </div>

      {(body.horizons.y1 || body.horizons.y3 || body.horizons.y5 || body.horizons.y10) && (
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Looking ahead
          </p>
          <div className="space-y-2.5">
            {(
              [
                ['1 year',   body.horizons.y1],
                ['3 years',  body.horizons.y3],
                ['5 years',  body.horizons.y5],
                ['10 years', body.horizons.y10],
              ] as [string, string][]
            )
              .filter(([, text]) => text)
              .map(([label, text]) => (
                <div key={label} className="flex gap-3">
                  <span className="text-xs text-muted-foreground tabular-nums w-14 flex-shrink-0 pt-px">
                    {label}
                  </span>
                  <p className="text-sm text-foreground/80 leading-relaxed">{text}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {(body.alignmentScore !== null || body.flags.length > 0) && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border">
          {body.alignmentScore !== null && (
            <span
              className={cn(
                'text-xs px-2 py-0.5 rounded-full border',
                body.alignmentScore >= 70
                  ? 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-700 dark:text-emerald-300'
                  : body.alignmentScore >= 45
                    ? 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-300'
                    : 'border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:border-red-700 dark:text-red-300',
              )}
            >
              Alignment {body.alignmentScore}/100
            </span>
          )}
          {body.flags.map(f => (
            <span
              key={f.bucketName}
              className="text-xs px-2 py-0.5 rounded-full border border-border text-muted-foreground"
            >
              {f.bucketName}{' '}
              {f.gapPct > 0
                ? `${f.gapPct.toFixed(0)}% under`
                : `${Math.abs(f.gapPct).toFixed(0)}% over`}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
